import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PageHeader } from '@/components/common';
import { goodsReceiptApi, PendingPO } from '@/api/services/goods-receipt.api';
import { locationsApi, Location } from '@/api/services/locations.api';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

export default function GoodsReceiptNew() {
  const navigate = useNavigate();
  const [pendingPOs, setPendingPOs] = useState<PendingPO[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [saving, setSaving] = useState(false);
  const [selectedPO, setSelectedPO] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [deliveryNote, setDeliveryNote] = useState('');
  const [carrier, setCarrier] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    Promise.all([goodsReceiptApi.getPendingPOs(), locationsApi.getAll({ limit: 100 })])
      .then(([pos, locs]) => {
        setPendingPOs(pos);
        setLocations(locs.data);
      })
      .catch(() => {
        toast.error('Failed to load reference data');
      });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedPO || !selectedLocation) {
      toast.error('Please select a PO and receiving location');
      return;
    }

    try {
      setSaving(true);
      await goodsReceiptApi.createFromPO({
        purchaseOrderId: selectedPO,
        locationId: selectedLocation,
        deliveryNoteNumber: deliveryNote || undefined,
        carrierName: carrier || undefined,
        notes: notes || undefined,
      });
      toast.success('Goods receipt created');
      navigate('/goods-receipt');
    } catch (error) {
      toast.error('Failed to create goods receipt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
      <PageHeader
        title="Create Goods Receipt"
        description="Details required to book a delivery in against an outstanding purchase order."
        actions={
          <Button variant="outline" onClick={() => navigate('/goods-receipt')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Goods Receipt
          </Button>
        }
      />

      <motion.div variants={fadeUp}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Source Purchase Order */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Source Purchase Order</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="po">Purchase Order *</Label>
              <Select value={selectedPO} onValueChange={setSelectedPO}>
                <SelectTrigger>
                  <SelectValue placeholder="Select PO to receive" />
                </SelectTrigger>
                <SelectContent>
                  {pendingPOs.map((po) => (
                    <SelectItem key={po.id} value={po.id}>
                      {po.poNumber} - {po.supplier?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Receiving Location */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Receiving Location</h3>
            <Separator />

            <div className="space-y-2">
              <Label htmlFor="location">Location *</Label>
              <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                <SelectTrigger>
                  <SelectValue placeholder="Select receiving location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Delivery Details */}
          <div className={cn(PANEL, 'p-6 space-y-4')}>
            <h3 className="text-base font-semibold">Delivery Details</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="deliveryNote">Delivery Note #</Label>
                <Input
                  id="deliveryNote"
                  value={deliveryNote}
                  onChange={(e) => setDeliveryNote(e.target.value)}
                  placeholder="DN-XXXXXXXX"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="carrier">Carrier Name</Label>
                <Input
                  id="carrier"
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  placeholder="e.g., FedEx"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes about this receipt..."
                rows={3}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/goods-receipt')}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Creating...' : 'Create Receipt'}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
